// Shared React Flow graph-building code for workflow visualizations. Originally lived entirely in
// the platform-admin WorkflowFlowchartPage.tsx (the editable definition-authoring flowchart);
// extracted here so the SAME layout/rendering logic can also power a READ-ONLY diagram view inside
// WorkflowSettingsCard.tsx (the tenant-admin workflow-customization card), without either module
// importing the other's RTK Query API types.
//
// `buildGraph()` accepts a small structural `GraphSource` shape rather than either concrete API
// type (`WorkflowDefinition` from workflowApi.ts, or `WorkflowCustomizationOut` from
// workflowEngineApi.ts) — both satisfy it structurally (same field names/semantics for
// stages/processes/statuses/transitions), so callers can pass either straight through with no
// mapping step.
import React from 'react';
import {
  Handle,
  Position,
  EdgeLabelRenderer,
  BaseEdge,
  getSmoothStepPath,
  MarkerType,
  Node,
  Edge,
} from '@xyflow/react';

// ─────────────────────────── Layout constants ─────────────────────────────

export const COL_W       = 210;
export const COL_GAP     = 60;
export const STATUS_H    = 74;
export const STATUS_GAP  = 18;
export const HEADER_Y    = 20;
export const HEADER_H    = 42;
export const STATUS_START_Y = HEADER_Y + HEADER_H + 22;

// ─────────────────────────── Transition colours ───────────────────────────

export const EDGE_COLOR: Record<string, string> = {
  standard:        '#3b82f6',
  backward:        '#f97316',
  skip:            '#a855f7',
  hold:            '#eab308',
  resume:          '#14b8a6',
  reopen:          '#6366f1',
  cancel:          '#ef4444',
  reject:          '#dc2626',
  corrective_loop: '#ec4899',
};

// ─────────────────────────── Structural source shape ──────────────────────
// Deliberately loose/local (not imported from either API module) — both WorkflowDefinition
// (platform definition authoring) and WorkflowCustomizationOut (tenant customization) satisfy this
// structurally, field-for-field.

export interface GraphStage {
  stage_code: string;
  name: string;
  display_order: number;
}

export interface GraphProcess {
  process_code: string;
  stage_code: string;
  display_order: number;
}

export interface GraphStatus {
  status_code: string;
  process_code: string;
  name: string;
  display_order: number;
  lifecycle_role: string;
  operational_type: string;
  is_optional?: boolean;
}

export interface GraphTransition {
  transition_code: string;
  source_status_code: string;
  target_status_code: string;
  action_label: string;
  category: string;
}

export interface GraphSource {
  stages: GraphStage[];
  processes: GraphProcess[];
  statuses: GraphStatus[];
  transitions: GraphTransition[];
}

// ─────────────────────────── Custom nodes ─────────────────────────────────
// Use loose prop types to avoid complex @xyflow/react v12 generic constraints.

export const StageLabelNode: React.FC<{ data: { stage: GraphStage } }> = ({ data }) => {
  const stage = data.stage;
  return (
    <div style={{
      background: 'rgba(55,65,81,0.45)', border: '1px solid rgba(107,114,128,0.35)',
      borderRadius: 8, padding: '7px 18px', textAlign: 'center', minWidth: COL_W, backdropFilter: 'blur(4px)',
    }}>
      <div style={{ fontWeight: 700, fontSize: 11, color: '#cbd5e1', letterSpacing: '0.07em', textTransform: 'uppercase' }}>
        {stage.name}
      </div>
      <div style={{ fontSize: 9, color: '#6b7280', fontFamily: 'monospace', marginTop: 1 }}>{stage.stage_code}</div>
    </div>
  );
};

export const StatusNode: React.FC<{ data: { status: GraphStatus }; selected?: boolean }> = ({ data, selected }) => {
  const s = data.status;
  const role = s.lifecycle_role;
  const borderColor = role === 'entry' ? '#3b82f6' : role === 'terminal' ? '#ef4444' : '#475569';
  const headerBg   = role === 'entry' ? 'rgba(59,130,246,0.18)' : role === 'terminal' ? 'rgba(239,68,68,0.15)' : 'rgba(71,85,105,0.18)';
  return (
    <div style={{
      border: `2px solid ${selected ? '#a78bfa' : borderColor}`, borderRadius: 10, overflow: 'hidden',
      background: '#1e293b', boxShadow: selected ? '0 0 0 3px rgba(167,139,250,0.3)' : '0 2px 8px rgba(0,0,0,0.4)',
      minWidth: COL_W - 20, cursor: 'pointer',
    }}>
      <Handle type="target" position={Position.Left}  style={{ background: borderColor, width: 10, height: 10 }} />
      <Handle type="source" position={Position.Right} style={{ background: borderColor, width: 10, height: 10 }} />
      <div style={{ background: headerBg, padding: '6px 10px', borderBottom: `1px solid ${borderColor}33` }}>
        <div style={{ fontWeight: 600, fontSize: 12, color: '#f1f5f9' }}>{s.name}</div>
        <div style={{ fontSize: 9, color: '#64748b', fontFamily: 'monospace', marginTop: 1 }}>{s.status_code}</div>
      </div>
      <div style={{ padding: '5px 10px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 999, background: borderColor + '33', color: borderColor, fontWeight: 600, textTransform: 'capitalize' }}>
          {role}
        </span>
        {s.operational_type !== 'normal' && (
          <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 999, background: '#1e293b', border: '1px solid #334155', color: '#94a3b8' }}>
            {s.operational_type.replace(/_/g, ' ')}
          </span>
        )}
        {s.is_optional && (
          <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 999, background: '#1e293b', border: '1px solid #334155', color: '#64748b' }}>optional</span>
        )}
      </div>
    </div>
  );
};

export const TransitionEdgeComponent: React.FC<{
  id: string; sourceX: number; sourceY: number; targetX: number; targetY: number;
  data?: { transition?: GraphTransition }; markerEnd?: string; style?: React.CSSProperties; selected?: boolean;
}> = ({ id, sourceX, sourceY, targetX, targetY, data, markerEnd, style, selected }) => {
  const [edgePath, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, borderRadius: 14 });
  const t = data?.transition;
  const color = EDGE_COLOR[t?.category ?? ''] ?? '#6b7280';
  return (
    <>
      <BaseEdge
        path={edgePath} markerEnd={markerEnd} id={id}
        style={{ ...style, strokeWidth: selected ? 2.5 : 1.5, filter: selected ? `drop-shadow(0 0 4px ${color})` : undefined }}
      />
      {t?.action_label && (
        <EdgeLabelRenderer>
          <div style={{
            position: 'absolute', transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
            background: '#0f172a', border: `1px solid ${color}55`, borderRadius: 4,
            padding: '2px 7px', fontSize: 9, color: '#94a3b8', pointerEvents: 'all',
            whiteSpace: 'nowrap', boxShadow: '0 1px 4px rgba(0,0,0,0.5)',
          }}>
            {t.action_label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Cast to satisfy ReactFlow's strict NodeTypes/EdgeTypes generics
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const nodeTypes: Record<string, React.FC<any>> = { stageLabel: StageLabelNode, statusNode: StatusNode };
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const edgeTypes: Record<string, React.FC<any>> = { transitionEdge: TransitionEdgeComponent };

// ─────────────────────────── Layout builder ───────────────────────────────

export function buildGraph(def: GraphSource): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const sortedStages = [...def.stages].sort((a, b) => a.display_order - b.display_order);

  sortedStages.forEach((stage, si) => {
    const x = si * (COL_W + COL_GAP);
    const stageProcs = [...def.processes]
      .filter(p => p.stage_code === stage.stage_code)
      .sort((a, b) => a.display_order - b.display_order);
    const stageStatuses: GraphStatus[] = [];
    for (const proc of stageProcs) {
      [...def.statuses]
        .filter(s => s.process_code === proc.process_code)
        .sort((a, b) => a.display_order - b.display_order)
        .forEach(s => stageStatuses.push(s));
    }

    nodes.push({
      id: `__stage_${stage.stage_code}`,
      type: 'stageLabel',
      position: { x, y: HEADER_Y },
      data: { stage } as Record<string, unknown>,
      draggable: false,
      selectable: false,
    });

    stageStatuses.forEach((status, idx) => {
      nodes.push({
        id: status.status_code,
        type: 'statusNode',
        position: { x: x + 10, y: STATUS_START_Y + idx * (STATUS_H + STATUS_GAP) },
        data: { status } as Record<string, unknown>,
      });
    });
  });

  def.transitions.forEach(t => {
    const color = EDGE_COLOR[t.category] ?? '#6b7280';
    edges.push({
      id: t.transition_code,
      source: t.source_status_code,
      target: t.target_status_code,
      type: 'transitionEdge',
      data: { transition: t } as Record<string, unknown>,
      animated: t.category === 'standard',
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
      style: { stroke: color },
    });
  });

  return { nodes, edges };
}
